import { useCallback, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ClipboardList,
  ExternalLink,
  Eye,
  Filter,
  RotateCw,
  SearchX,
  ShoppingCart,
  PhoneCall,
} from "lucide-react";
import { appConfig } from "@ir/config";
import type { LeadQuote } from "@ir/types";
import {
  deriveMarketplacePricingSummary,
  formatDateTime,
  formatPricingOgiIndicator,
  formatPricingOgiTooltip,
  fullName,
  latestQuoteActivityAt,
  searchQuotes,
} from "@ir/services";
import {
  Badge,
  Button,
  CopyButton,
  CopyableId,
  DataTable,
  IconButton,
  PageHeader,
  Pagination,
  SearchInput,
  Select,
  StatusPill,
  Toolbar,
  type Column,
  type SortState,
} from "@ir/ui";
import { ColumnVisibilityButton } from "../components/ColumnVisibilityButton";
import { useAsyncData } from "../hooks/useAsyncData";
import { useColumnVisibility } from "../hooks/useColumnVisibility";

const PAGE_SIZE = 25;
type QuoteSortBy = "created_at" | "updated_at" | "reference" | "type";

function quoteSortByForColumn(columnId: string): QuoteSortBy {
  if (columnId === "customer_reference") return "reference";
  if (columnId === "updated") return "updated_at";
  if (columnId === "created") return "created_at";
  if (["created_at", "reference", "type"].includes(columnId)) return columnId as QuoteSortBy;
  return "created_at";
}

const FILTERABLE_STATES = ["Pending", "Quoted", "Partial", "Processed", "Failed"];

const STATE_OPTIONS = [
  { value: "", label: "All states" },
  ...FILTERABLE_STATES.map((state) => ({ value: state, label: state })),
];

const PROVIDER_DOT_COLOURS: Record<string, string> = {
  granite: "bg-orange-500 ring-orange-500/20",
  inshur: "bg-sky-500 ring-sky-500/20",
  kinetiq: "bg-violet-500 ring-violet-500/20",
  ogi: "bg-gold-500 ring-gold-500/20",
};

/** Derived state: a quote counts as quoted once a provider has priced it. */
function quoteState(quote: LeadQuote): string {
  const summary = deriveMarketplacePricingSummary(quote.base_quotes ?? []);
  if (summary.quotedProviderCount > 0 || summary.allExpectedProvidersResolved) {
    return summary.overallStatus;
  }
  return quote.state ?? "Pending";
}

function quoteOgiReference(quote: LeadQuote): string | null {
  const ogiBaseQuote = (quote.base_quotes ?? []).find(
    (baseQuote) => baseQuote.provider?.toLowerCase() === "opengi",
  );

  return (
    ogiBaseQuote?.provider_metadata?.ogi?.broker_policy_reference ??
    ogiBaseQuote?.provider_metadata?.ogi?.send_to_broker_ref ??
    ogiBaseQuote?.provider_metadata?.ogi?.prospect_reference ??
    null
  );
}

function ProviderStatusDots({ quote }: { quote: LeadQuote }) {
  const summary = deriveMarketplacePricingSummary(quote.base_quotes ?? []);
  const items = [
    ...summary.providerBreakdown.map((provider) => ({
      label:
        provider.provider === "inshur"
          ? "INSHUR"
          : provider.provider === "kinetiq"
            ? "Kinetiq"
            : "Granite",
      key: provider.provider,
      ok: provider.returnedQuote,
    })),
    {
      label: "OGI",
      key: "ogi",
      ok: summary.ogiSentSuccessfully,
    },
  ];

  return (
    <div className="mt-1 flex items-center justify-center gap-1">
      {items.map((item) => (
        <span
          key={item.label}
          title={`${item.label}: ${item.ok ? "Yes" : "No"}`}
          className={
            item.ok
              ? `size-2.5 rounded-full ring-2 ${PROVIDER_DOT_COLOURS[item.key] ?? "bg-success-500 ring-success-500/20"}`
              : "size-2.5 rounded-full bg-content-subtle/30"
          }
        />
      ))}
    </div>
  );
}

function BuyIntentStatus({ quote }: { quote: LeadQuote }) {
  const buyIntent = quote.buy_intent;
  if (!buyIntent) return <span className="text-content-subtle">—</span>;

  const normalizedStatus = buyIntent.status.toLowerCase();
  const tone =
    normalizedStatus === "queued"
      ? "success"
      : normalizedStatus === "failed"
        ? "danger"
        : "warning";

  const title = buyIntent.noetica_queue_id
    ? `Noetica queue ${buyIntent.noetica_queue_id}`
    : buyIntent.error ?? `Buy intent ${buyIntent.status}`;

  return (
    <span title={title}>
      <Badge icon={ShoppingCart} tone={tone}>
        {buyIntent.status}
      </Badge>
    </span>
  );
}

function NoeticaStatus({ quote }: { quote: LeadQuote }) {
  const delivery = quote.noetica_delivery;
  const status = delivery?.status ?? (quote.dialer_queue ? "Queued" : "Not attempted");
  const normalizedStatus = status.toLowerCase();
  const tone =
    normalizedStatus === "queued"
      ? "success"
      : normalizedStatus === "failed"
        ? "danger"
        : normalizedStatus === "pendingretry"
          ? "warning"
          : "neutral";
  const title = quote.dialer_queue
    ? `Noetica queue ${quote.dialer_queue}`
    : delivery?.error ?? status;

  return (
    <span title={title}>
      <Badge icon={PhoneCall} tone={tone}>
        {status === "PendingRetry" ? "Retry pending" : status}
      </Badge>
    </span>
  );
}

export function QuotesPage() {
  // Filters live in the URL so an operator can share or bookmark a search.
  const [searchParams, setSearchParams] = useSearchParams();
  const [refreshToken, setRefreshToken] = useState(0);

  const searchTerm = searchParams.get("q") ?? "";
  const state = searchParams.get("state") ?? "";
  const page = Number(searchParams.get("page") ?? "1") || 1;
  const sortBy = searchParams.get("sortBy") ?? "created_at";
  const sortDirection = searchParams.get("sortDirection") === "asc" ? "asc" : "desc";

  const updateParams = useCallback(
    (changes: Record<string, string | null>) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(changes)) {
            if (value === null || value === "") next.delete(key);
            else next.set(key, value);
          }
          // Any filter change invalidates the current page number.
          if (!("page" in changes)) next.delete("page");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const result = useAsyncData(
    (signal) =>
      searchQuotes({
        searchTerm,
        state,
        pageNumber: page,
        pageSize: PAGE_SIZE,
        sortBy: quoteSortByForColumn(sortBy),
        sortDirection,
        signal,
      }),
    [searchTerm, state, page, sortBy, sortDirection, refreshToken],
  );

  const columns = useMemo<Column<LeadQuote>[]>(
    () => [
      {
        id: "state",
        header: "State",
        width: "8.5rem",
        cell: (quote) => (
          <div className="flex flex-col items-start whitespace-nowrap">
            <StatusPill status={quoteState(quote)} />
            <ProviderStatusDots quote={quote} />
          </div>
        ),
      },
      {
        id: "pricing",
        header: "PRICING(OGI)",
        width: "9rem",
        cell: (quote) => {
          const summary = deriveMarketplacePricingSummary(quote.base_quotes ?? []);
          return (
            <span
              className="cursor-help whitespace-nowrap tabular text-content-muted underline decoration-dotted underline-offset-4"
              title={formatPricingOgiTooltip(summary)}
            >
              {formatPricingOgiIndicator(summary)}
            </span>
          );
        },
      },
      {
        id: "buy_intent",
        header: "Buy intent",
        width: "7.5rem",
        hideBelow: "xl",
        cell: (quote) => (
          <span className="block max-w-28 truncate whitespace-nowrap">
            <BuyIntentStatus quote={quote} />
          </span>
        ),
      },
      {
        id: "noetica",
        header: "Noetica",
        width: "9rem",
        hideBelow: "xl",
        cell: (quote) => <NoeticaStatus quote={quote} />,
      },
      {
        id: "client",
        header: "Client",
        width: "18rem",
        cell: (quote) => {
          const driver = quote.driver?.[0];
          const email = driver?.email ?? "";
          return (
            <div className="min-w-0 max-w-72">
              <p className="truncate font-medium text-content">
                {fullName([driver?.title, driver?.first_name, driver?.last_name])}
              </p>
              <div className="flex min-w-0 items-center gap-1">
                <p className="min-w-0 truncate text-xs text-content-subtle" title={email || undefined}>
                  {email || "No email"}
                </p>
                {email && <CopyButton value={email} label="Copy email" className="size-6" />}
              </div>
            </div>
          );
        },
      },
      {
        id: "telephone",
        header: "Telephone",
        width: "9rem",
        hideBelow: "lg",
        cell: (quote) => (
          <span className="whitespace-nowrap tabular text-content-muted">
            {quote.driver?.[0]?.telephone ?? "—"}
          </span>
        ),
      },
      {
        id: "customer_reference",
        header: "Ref",
        mobileLabel: "Customer ref",
        width: "8rem",
        hideBelow: "lg",
        sortable: true,
        cell: (quote) =>
          quote.reference ? (
            <CopyableId value={quote.reference} />
          ) : (
            <span className="text-content-subtle">—</span>
          ),
      },
      {
        id: "type",
        header: "Type",
        hideBelow: "xl",
        sortable: true,
        cell: (quote) => (
          <span className="text-content-muted">{quote.type ?? "—"}</span>
        ),
      },
      {
        id: "created",
        header: "Created",
        width: "11rem",
        hideBelow: "md",
        sortable: true,
        cell: (quote) => (
          <span className="whitespace-nowrap text-content-muted">
            {formatDateTime(quote.created_at)}
          </span>
        ),
      },
      {
        id: "updated",
        header: "Updated",
        hideBelow: "xl",
        sortable: true,
        cell: (quote) => (
          <span className="whitespace-nowrap text-content-muted">
            {formatDateTime(latestQuoteActivityAt(quote))}
          </span>
        ),
      },
      {
        id: "customer_email",
        header: "Email",
        hideBelow: "xl",
        width: "9rem",
        cell: (quote) => (
          <span
            className="whitespace-nowrap text-content-muted"
            title={
              quote.customer_quote_email_sent_at
                ? `Sent ${formatDateTime(quote.customer_quote_email_sent_at)}`
                : quote.customer_quote_notification_claimed_at
                  ? `Delivery pending or unconfirmed since ${formatDateTime(quote.customer_quote_notification_claimed_at)}`
                : "No customer quote email sent"
            }
          >
            {quote.customer_quote_email_sent_at
              ? "Sent"
              : quote.customer_quote_notification_claimed_at
                ? "Unconfirmed"
                : "Not sent"}
          </span>
        ),
      },
      {
        id: "ogi_reference",
        header: "OGI Ref",
        hideBelow: "xl",
        cell: (quote) => {
          const ogiReference = quoteOgiReference(quote);
          return ogiReference ? (
            <CopyableId value={ogiReference} />
          ) : (
            <span className="text-content-subtle">—</span>
          );
        },
      },
      {
        id: "actions",
        header: "",
        mobileLabel: "Actions",
        align: "right",
        width: "6rem",
        cell: (quote) => {
          const email = quote.driver?.[0]?.email ?? "";
          const customerUrl = `${appConfig.publicQuoteScreenUrl}/?driveremail=${encodeURIComponent(
            email,
          )}&quoteid=${encodeURIComponent(quote.id)}`;

          return (
            <div className="flex items-center justify-end gap-1">
              <Link to={`/quotes/${quote.id}`} onClick={(event) => event.stopPropagation()}>
                <IconButton icon={Eye} label="View quote details" size="sm" />
              </Link>
              <a
                href={customerUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(event) => event.stopPropagation()}
              >
                <IconButton
                  icon={ExternalLink}
                  label="Open the customer quote screen"
                  size="sm"
                />
              </a>
            </div>
          );
        },
      },
    ],
    [],
  );

  const columnVisibility = useColumnVisibility(
    "quotes",
    columns,
    ["state", "pricing", "client", "telephone", "customer_reference", "created"],
  );

  const sort: SortState = {
    columnId:
      sortBy === "reference"
        ? "customer_reference"
        : sortBy === "updated_at"
          ? "updated"
          : sortBy === "created_at"
            ? "created"
          : sortBy,
    direction: sortDirection,
  };

  const updateSort = (next: SortState) => {
    const nextSortBy = quoteSortByForColumn(next.columnId);
    updateParams({ sortBy: nextSortBy, sortDirection: next.direction });
  };

  const hasFilters = Boolean(searchTerm || state);

  return (
    <>
      <PageHeader
        icon={ClipboardList}
        title="Quotes"
        description="Every quote request that has reached the marketplace."
        actions={
          <Button
            variant="secondary"
            icon={RotateCw}
            loading={result.refreshing}
            onClick={() => setRefreshToken((token) => token + 1)}
          >
            Refresh
          </Button>
        }
      >
        <Toolbar>
          <SearchInput
            className="flex-1"
            value={searchTerm}
            onChange={(value) => updateParams({ q: value })}
            placeholder="Search by reference, name, phone, email or postcode"
            ariaLabel="Search quotes"
          />
          <Select
            className="sm:w-48"
            icon={Filter}
            options={STATE_OPTIONS}
            value={state}
            aria-label="Filter by state"
            onChange={(event) => updateParams({ state: event.target.value })}
          />
          <ColumnVisibilityButton
            columns={columnVisibility.selectableColumns}
            visibleColumnIds={columnVisibility.visibleColumnIds}
            minimumReached={columnVisibility.minimumReached}
            onToggle={columnVisibility.toggleColumn}
            onReset={columnVisibility.resetColumns}
          />
          {hasFilters && (
            <Button variant="ghost" onClick={() => setSearchParams({}, { replace: true })}>
              Clear
            </Button>
          )}
        </Toolbar>
      </PageHeader>

      <DataTable
        caption="Quotes"
        columns={columnVisibility.visibleColumns}
        rows={result.data?.items ?? []}
        rowKey={(quote) => quote.id}
        loading={result.loading}
        refreshing={result.refreshing}
        error={result.error}
        onRetry={result.reload}
        sort={sort}
        onSortChange={updateSort}
        emptyIcon={SearchX}
        emptyTitle={hasFilters ? "No quotes match those filters" : "No quotes yet"}
        emptyDescription={
          hasFilters
            ? "Try a broader search term, or clear the state filter."
            : "Quotes will appear here as customers complete the form."
        }
      />

      {result.data && (
        <Pagination
          currentPage={result.data.pageNumber}
          totalPages={result.data.totalPages}
          totalCount={result.data.totalCount}
          pageSize={result.data.pageSize}
          disabled={result.refreshing}
          onPageChange={(next) => updateParams({ page: String(next) })}
        />
      )}
    </>
  );
}
